//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import {
  FacebookPostAttemptFailure,
  InstagramPostAttemptFailure,
  PinterestPostAttemptFailure,
  ScheduledPostAttemptData,
  TikTokPostAttemptFailure,
  TweetPostAttemptFailure,
  TwitterApiError,
  TwitterPosterException,
} from '@bvengineering/universal-scheduling-gateway-service-types';

import { ScheduledItemFailureStatus } from '@models/ScheduledItemFailureStatus';
import { OmitStrict } from '@utils/types/OmitStrict';

export enum UniversalScheduledPostAttemptDataType {
  FacebookPostAttemptFailure = 'v2:facebook-automatic-post-failure',
  InstagramPostAttemptFailure = 'v2:instagram-automatic-post-failure',
  PinterestFailure = 'v2:pinterest-organic-pin-failure',
  TwitterFailure = 'v2:twitter-automatic-post-failure',
  TikTokFailure = 'v2:tiktok-video-automatic-failure',
}

export type TScheduledPostAttemptData =
  | IFacebookPostAttemptFailure
  | IPinterestPostAttemptFailure
  | IInstagramPostAttemptFailure
  | ITwitterPostAttemptFailure
  | ITikTokPostAttemptFailure;

export interface IScheduledPostAttemptDataWithPostType
  extends Omit<ScheduledPostAttemptData, '@type'> {
  '@typev2': UniversalScheduledPostAttemptDataType;
}

export interface IFacebookPostAttemptFailure
  extends ScheduledPostAttemptData,
    OmitStrict<FacebookPostAttemptFailure, 'error'> {
  error:
    | 'aspect_ratio'
    | 'daily_publishing_limit'
    | 'feature_block'
    | 'image_size'
    | 'intermittent_upload_failure'
    | 'token'
    | 'unknown_graph_api'
    | 'unclassified';
  '@typev2': UniversalScheduledPostAttemptDataType.FacebookPostAttemptFailure;
}

export interface IPinterestPostAttemptFailure
  extends IScheduledPostAttemptDataWithPostType,
    PinterestPostAttemptFailure {
  '@typev2': UniversalScheduledPostAttemptDataType.PinterestFailure;
}

export interface ITwitterPostAttemptFailure
  extends IScheduledPostAttemptDataWithPostType,
    TweetPostAttemptFailure {
  twitterPosterException: ITwitterPosterException;
  '@typev2': UniversalScheduledPostAttemptDataType.TwitterFailure;
}

export interface ITwitterPosterException extends TwitterPosterException {
  twitterApiError?: TwitterApiError;
  message: string;
}

export interface IInstagramPostAttemptFailure
  extends ScheduledPostAttemptData,
    OmitStrict<InstagramPostAttemptFailure, 'error'> {
  '@type': UniversalScheduledPostAttemptDataType.InstagramPostAttemptFailure;
  error: string;
}

export interface ITikTokPostAttemptFailure
  extends ScheduledPostAttemptData,
    TikTokPostAttemptFailure {
  '@typev2': UniversalScheduledPostAttemptDataType.TikTokFailure;
}
